import { Component, h, Element, Prop, Listen, Event, type EventEmitter} from '@stencil/core';
import guidedSearchStore from '../guided/guided-search-store';
import { UIaaSSmartSelector } from './uiaas-smart-selector';
import { countrySelectorWidgetEventEmitter } from './utils';
import { searchOverflowToggler } from '../../utils/helpers/overflowToggler';

@Component({
  tag: 'uiaas-smart-country-selector-widget',
  shadow: true,
  styleUrl: 'uiaas-smart-country-selector-widget.scss',
})
export class SmartCountrySelectorWidget {
  @Prop() readonly isGuidedSearch: boolean = false;
  @Prop() readonly guidedSearchTerm: string = '';
  @Prop() readonly contentText: string;
  @Prop() readonly accessibilityMode: boolean;
  @Prop() readonly language: string;
  @Prop() readonly totalHits?: string | undefined;

  @Element() element;

  private removeComponentFromParent() {
      this.element.parentNode.removeChild(this.element);
      searchOverflowToggler(false);
  }

  @Event({
    eventName: 'search:globalDecommissioningSelector',
  })
    SearchGlobalDecommissioningSelectorEmitterSRP: EventEmitter;

  @Event({
    eventName: 'guided:globalDecommissioningSelector',
  })
    SearchGlobalDecommissioningSelectorEmitterGuided: EventEmitter;

  @Listen('closeButtonClicked', { target: 'window' })
  handleCloseAction() {
      this.removeComponentFromParent();
  }

  @Listen('countryLabelClicked', { target: 'window' })
  handleRedirection(e) {
      const actualLocation = window.location.href;
      const countrySelectorDataArray = e.detail.locale.split('_');
    
      const sanitizeString = (input: string) => {
      // Allow only specific safe characters for country and language code
          return input.replace(/[^a-zA-Z]/g, '');
      };

      const countrySelectorDataObject: {
      language: string;
      country: string;
    } = {
      language: sanitizeString(countrySelectorDataArray[0] || ''),
      country: sanitizeString(countrySelectorDataArray[1] || ''),
    };

      const { country, language } = countrySelectorDataObject;

      if(e.detail.consumerName == 'search') {
        const anchorElement = document.createElement('a');
        const newLocation = this.createRedirectionUrl(
          actualLocation,
          countrySelectorDataObject
        );

        const sanitizeUrl = (url: string, country?: string, language?: string) => {
            try {
              const parsedUrl = new URL(url);
              if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
                throw new Error('Invalid protocol');
              }
              return parsedUrl.toString();
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('Invalid URL detected:', url);
              return `${country}/${language}/`; // Safe fallback
            }
        };

        anchorElement.target = '_blank';
        anchorElement.href = sanitizeUrl(newLocation, country, language);
        anchorElement.click();
        anchorElement.remove();
        window.URL.revokeObjectURL(newLocation);
      }
  }

  private isUrlValid = (string) => {
      try {
        new URL(string);
        return true;
      } catch (err) {
        return false;
      }
  };

  private replaceLocation = (
    url: string,
    countrySelectorDataObject: { language: string; country: string }
  ) =>
    url.replace(
      /\/ww\/(en|fr)/,
      `/${countrySelectorDataObject.country}/${countrySelectorDataObject.language}`
    );

  private createRedirectionUrl(
    actualLocation,
    countrySelectorDataObject: { language: string; country: string }
  ) {
      const gsTerm = guidedSearchStore.get('gsTerm');
      const fallbackUrl = 'https://www.se.com';
      const location = `${countrySelectorDataObject.country}/${countrySelectorDataObject.language}`;

      countrySelectorWidgetEventEmitter(
        this.isGuidedSearch,
        this.totalHits,
        this.SearchGlobalDecommissioningSelectorEmitterGuided,
        this.SearchGlobalDecommissioningSelectorEmitterSRP,
        location
      );

      if (this.isGuidedSearch && gsTerm) {
        const parsedUrl = this.isUrlValid(actualLocation)
          ? new URL(actualLocation)
          : new URL(fallbackUrl);

        if (parsedUrl.searchParams.get('q')) {
          parsedUrl.searchParams.set('q', gsTerm);

          return this.replaceLocation(
            parsedUrl.toString(),
            countrySelectorDataObject
          );
        }

        const newUrl = new URL(parsedUrl.origin);

        newUrl.hostname = parsedUrl.hostname;
        newUrl.pathname = `/${location}${/search/}`;
        newUrl.searchParams.set('q', gsTerm);

        return newUrl.toString();
      }

      return this.replaceLocation(actualLocation, countrySelectorDataObject);
  }

  render() {
      return (
        <div>
          <div class={'smart-country-selector-widget'}>
            <UIaaSSmartSelector
              title={this.contentText}
              language={this.language}
              accessibilityMode={Boolean(this.accessibilityMode)}
            />
          </div>
          <div
            class={'smart-country-selector-widget__overlay'}
            onClick={() => {
                this.removeComponentFromParent();
                window.dispatchEvent(new CustomEvent('closeButtonClicked'));
            }}
          />
        </div>
      );
  }
}
